import { useEffect, useRef, useState } from 'react';
import { MetamodelVisualization } from './MetamodelVisualization';
import { ArdoqId, MetamodelContent } from '@ardoq/api-types';
import styled from 'styled-components';
import { TertiaryButton } from '@ardoq/button';
import {
  CloseIcon,
  ICON_SELECTOR,
  SpinnerIcon,
  VisibilityIcon,
} from '@ardoq/icons';
import { colors, s8 } from '@ardoq/design-tokens';
import { FlexBox } from '@ardoq/layout';
import { FormWrapper } from '@ardoq/forms';
import { isEqual } from 'lodash';
import { Logger } from '@ardoq/logging';
import { isArdoqError } from '@ardoq/common-helpers';
import { metamodelApi } from '@ardoq/api';
import {
  MetamodelPreviewLabels,
  defaultMetamodelPreviewLabels,
} from './labels';

const METAMODEL_CONTAINER_HEIGHT = 367;
const MetamodelContainer = styled.div`
  display: flex;
  border: 1px solid ${colors.grey300};
  border-radius: ${s8};
  height: ${METAMODEL_CONTAINER_HEIGHT}px;
  > ${ICON_SELECTOR} {
    /* Centering the loading spinner */
    margin: auto;
  }
`;

const useMetamodel = (
  selectedWorkspaceIds: ArdoqId[],
  logError: Logger,
  onlyFetchOnPreviewOpen: boolean
) => {
  const [metamodel, setMetamodel] = useState<MetamodelContent | null>(null);
  const [isFetchingMetamodel, setIsFetchingMetamodel] = useState(false);
  const [shouldShowPreview, setShouldShowPreview] = useState(false);
  const workspaceIdsThatHasBeenUsedToFetchMetamodel = useRef<ArdoqId[]>([]);

  useEffect(() => {
    (async () => {
      const hasSelectedWorkspacesChanged = !isEqual(
        selectedWorkspaceIds,
        workspaceIdsThatHasBeenUsedToFetchMetamodel.current
      );
      const shouldFetch =
        !onlyFetchOnPreviewOpen ||
        (onlyFetchOnPreviewOpen && shouldShowPreview);

      if (
        shouldFetch &&
        hasSelectedWorkspacesChanged &&
        !isFetchingMetamodel &&
        selectedWorkspaceIds.length
      ) {
        workspaceIdsThatHasBeenUsedToFetchMetamodel.current =
          selectedWorkspaceIds;
        setIsFetchingMetamodel(true);

        const metamodelContent =
          await metamodelApi.generate(selectedWorkspaceIds);

        if (isArdoqError(metamodelContent)) {
          return logError(metamodelContent);
        }

        setMetamodel(metamodelContent);
        setIsFetchingMetamodel(false);
      }
    })();
  }, [
    shouldShowPreview,
    isFetchingMetamodel,
    selectedWorkspaceIds,
    logError,
    onlyFetchOnPreviewOpen,
  ]);

  return {
    metamodel: selectedWorkspaceIds.length ? metamodel : null,
    isFetchingMetamodel,
    shouldShowPreview: selectedWorkspaceIds.length ? shouldShowPreview : false,
    setShouldShowPreview,
  };
};

type MetamodelPreviewButtonInterface = {
  shouldShowPreview: boolean;
  setShouldShowPreview: (shouldShowPreview: boolean) => void;
  isLoading: boolean;
  isDisabled: boolean;
  clickId?: string;
  closePreviewLabel: string;
  previewMetamodelLabel: string;
};

const MetamodelPreviewButton = ({
  shouldShowPreview,
  setShouldShowPreview,
  isLoading,
  isDisabled,
  clickId,
  closePreviewLabel,
  previewMetamodelLabel,
}: MetamodelPreviewButtonInterface) => (
  <TertiaryButton
    onClick={() => setShouldShowPreview(!shouldShowPreview)}
    isLoading={isLoading}
    isDisabled={isDisabled}
    data-click-id={clickId}
  >
    {shouldShowPreview ? <CloseIcon /> : <VisibilityIcon />}
    {shouldShowPreview ? closePreviewLabel : previewMetamodelLabel}
  </TertiaryButton>
);

export type MetamodelPreviewProps = {
  onlyFetchOnPreviewOpen?: boolean;
  workspaceIds: ArdoqId[];
  clickId?: string;
  className?: string;
  logError: Logger;
  labels?: MetamodelPreviewLabels;
};

export const MetamodelPreview = ({
  onlyFetchOnPreviewOpen = false,
  workspaceIds,
  clickId,
  className,
  logError,
  labels,
}: MetamodelPreviewProps) => {
  const { closePreviewLabel, previewMetamodelLabel } = {
    ...defaultMetamodelPreviewLabels,
    ...labels,
  };
  const {
    metamodel,
    isFetchingMetamodel,
    shouldShowPreview,
    setShouldShowPreview,
  } = useMetamodel(workspaceIds, logError, onlyFetchOnPreviewOpen);

  const isPreviewButtonDisabled =
    (!onlyFetchOnPreviewOpen && !metamodel) ||
    (onlyFetchOnPreviewOpen && workspaceIds.length === 0);

  return (
    <FlexBox flexDirection="column" gap="xlarge" className={className}>
      <FormWrapper>
        <MetamodelPreviewButton
          shouldShowPreview={shouldShowPreview}
          setShouldShowPreview={setShouldShowPreview}
          isLoading={isFetchingMetamodel}
          isDisabled={isPreviewButtonDisabled}
          clickId={clickId}
          closePreviewLabel={closePreviewLabel}
          previewMetamodelLabel={previewMetamodelLabel}
        />
      </FormWrapper>
      {shouldShowPreview ? (
        <MetamodelContainer>
          {metamodel && !isFetchingMetamodel ? (
            <MetamodelVisualization
              metamodelReferences={metamodel.referenceTypes}
              metamodelComponents={metamodel.componentTypes}
              metamodelWorkspaces={metamodel.workspaces}
              metamodelAssetFolders={metamodel.workspaceFolders}
              hasZoomControls
              hasLeftMenu={false}
            />
          ) : (
            <SpinnerIcon />
          )}
        </MetamodelContainer>
      ) : null}
    </FlexBox>
  );
};
